'use client'

import React, { useId } from 'react'

import { useExtractSpaceProps } from '../../hooks'
import { ErrorOutline, InfoOutline } from '../../icons'
import { SpaceProps } from '../../themes'
import { Field } from '../Field'
import { TextInput, TextInputProps } from './TextInput'

export interface TextFieldProps
  extends SpaceProps,
    Omit<TextInputProps, 'hasError'> {
  /**
   * Class name
   */
  className?: string

  /**
   * TextField label
   */
  label?: string

  /**
   * Required flag
   */
  required?: boolean

  /**
   * HTML required flag
   */
  htmlRequired?: boolean

  /**
   * Icon to display before the error message
   */
  errorIcon?: React.ReactNode

  /**
   * Error messages
   */
  errorMessages?: string | string[]

  /**
   * Icon to display before the caption message
   */
  captionIcon?: React.ReactNode

  /**
   * Caption
   */
  captionMessages?: string | string[]
}

export const TextFieldInner = (
  {
    id,
    className,
    label,
    required,
    htmlRequired,
    errorIcon = <ErrorOutline />,
    errorMessages,
    captionIcon = <InfoOutline />,
    captionMessages,
    isMultipleLine,
    ...props
  }: TextFieldProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const autoId = useId()
  const inputId = id ?? autoId

  // Extract space props from input props
  const [spaceProps, inputProps] = useExtractSpaceProps(props)

  return (
    <Field className={className} {...spaceProps}>
      {label && (
        <Field.Label id={inputId} required={required}>
          {label}
        </Field.Label>
      )}

      <Field.Content mt={isMultipleLine ? 2 : 0}>
        <TextInput
          id={inputId}
          ref={ref}
          hasError={!!errorMessages}
          isMultipleLine={isMultipleLine}
          required={htmlRequired}
          {...inputProps}
        />
      </Field.Content>

      <Field.Messages
        mt={2}
        type="error"
        messages={errorMessages}
        icon={errorIcon}
      />

      <Field.Messages
        mt={errorMessages || (errorMessages && errorMessages.length) ? 1 : 2}
        type="caption"
        messages={captionMessages}
        icon={captionIcon}
      />
    </Field>
  )
}

export const TextField = React.forwardRef(TextFieldInner) as (
  props: TextFieldProps & { ref?: React.ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof TextFieldInner>
